import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import type { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import moment from "moment";
import { Fragment, type MouseEvent } from "react";
import { LinkCellRenderer } from "@/controls/ag-grid/components/ButtonCellRenderer";
import { CategoryTag } from "../components/table/CategoryTag";
import { GridButtons } from "../components/table/GridButtons";
import type { StrandLossByShift } from "../models/StrandLossByShift";

const cellStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

interface UseTableDataProps {
  justifyData: (data: StrandLossByShift) => void;
  showContextMenu: (
    event: MouseEvent<HTMLButtonElement>,
    data: StrandLossByShift,
  ) => void;
}

const HeatLinksCellRenderer = (
  params: ICellRendererParams<StrandLossByShift>,
) => {
  const heats = params.data?.Heats ?? [];

  return (
    <span>
      {heats.map((heat, index) => (
        <Fragment key={heat.ProcessId}>
          {index > 0 && ", "}
          {LinkCellRenderer({
            ...params,
            value: heat.HeatName,
            link: `/caster-data-entry/${heat.ProcessId}`,
          })}
        </Fragment>
      ))}
    </span>
  );
};

export const useTableData = ({
  justifyData,
  showContextMenu,
}: UseTableDataProps) => {
  const columnDefs: ColDef<StrandLossByShift>[] = [
    {
      field: "Heats",
      headerName: "Heat",
      minWidth: 220,
      flex: 2,
      cellStyle,
      cellRenderer: HeatLinksCellRenderer,
    },
    {
      field: "Strand",
      headerName: "Strand",
      minWidth: 75,
      maxWidth: 85,
      flex: 1,
      cellStyle,
    },
    {
      field: "Start",
      headerName: "Start",
      valueGetter: (params) =>
        dxtToLocalServerTime(params.data?.Start, "HH:mm:ss") || "",
      minWidth: 90,
      maxWidth: 120,
      flex: 1,
      cellStyle,
    },
    {
      field: "End",
      headerName: "End",
      valueGetter: (params) =>
        dxtToLocalServerTime(params.data?.End, "HH:mm:ss") || "",
      minWidth: 90,
      maxWidth: 100,
      flex: 1,
      cellStyle,
    },
    {
      field: "Duration",
      headerName: "Duration",
      valueFormatter: (params) => {
        if (!params.value) return "";
        const duration = moment.duration(params.value, "seconds");
        return `${Math.floor(duration.asMinutes())}m ${duration.seconds()}s`;
      },
      minWidth: 95,
      maxWidth: 105,
      flex: 1,
      cellStyle,
    },
    {
      headerName: "Category",
      valueGetter: (params) => ({
        name: params.data?.StrandLossCategoryName ?? "",
        color: params.data?.StrandLossCategoryColor ?? "",
      }),
      cellRenderer: CategoryTag,
      minWidth: 110,
      maxWidth: 120,
      editable: false,
      flex: 1,
      cellStyle,
    },
    {
      field: "StrandLossCodeName",
      headerName: "Code",
      minWidth: 180,
      editable: false,
      flex: 2,
      cellStyle,
    },
    {
      field: "Comments",
      headerName: "Comments",
      minWidth: 240,
      flex: 3,
      wrapText: true,
      autoHeight: true,
      cellStyle,
    },
    {
      field: "JustifiedByUser",
      headerName: "User",
      minWidth: 100,
      maxWidth: 150,
      editable: false,
      flex: 1,
      cellStyle,
    },
    {
      field: "JustifiedDateTime",
      headerName: "Justification Date",
      valueGetter: (params) =>
        dxtToLocalServerTime(params.data?.JustifiedDateTime, "MM-dd HH:mm"),
      minWidth: 150,
      maxWidth: 150,
      flex: 1,
      cellStyle,
    },
    {
      cellRenderer: GridButtons,
      cellRendererParams: (params: ICellRendererParams<StrandLossByShift>) => ({
        params,
        justifyData,
        showContextMenu: (event: MouseEvent<HTMLButtonElement>) => {
          if (params.data) showContextMenu(event, params.data);
        },
      }),
      wrapText: false,
      wrapHeaderText: false,
      headerName: "",
      sortable: false,
      flex: 1,
      maxWidth: 150,
      minWidth: 150,
      filter: false,
      cellStyle,
    },
  ];

  return { columnDefs };
};
